/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { FormGroup, Label, Input } from "reactstrap";
import moment from "moment";
import "react-dates/initialize";
import { SingleDatePicker } from "react-dates";

const Schedule = ({
  schedule, // cron format or empty string
  setSchedule,
  timezone,
  setTimezone,
  expiration,
  setExpiration,
}) => {
  const [minDate, setMinDate] = useState(null);
  const [defaultDate, setDefaultDate] = useState(null);
  const [maxDate, setMaxDate] = useState(null);
  const [datePickerFocus, setDatePickerFocus] = useState(false);
  const [invalidScheduleMessage, setInvalidScheduleMessage] = useState("");

  // Inputs
  const [frequency, setFrequency] = useState("none");
  const [weekday, setWeekday] = useState(0);
  const [day, setDay] = useState(0);
  const [time, setTime] = useState(0);
  const [expire, setExpire] = useState(null); // moment date obj

  const handleExpireDateChange = (date) => {
    setInvalidScheduleMessage("");
    if (date === null) {
      setInvalidScheduleMessage(
        `Please choose a date between today and ${maxDate.format(
          "MM/DD/YYYY"
        )}.`
      );
      setExpire(defaultDate);
    } else {
      setExpire(date);
      setExpiration(date.format("X"));
    }
  };

  // convert saved cron schedule to input values
  useEffect(() => {
    if (!schedule) {
      setFrequency("none");
      setTime(0);
      setWeekday(0);
      setDay(1);
      setExpiration(null);
      setExpire(defaultDate);
    } else {
      const cron = schedule.split(" ");
      setTime(cron[1]);
      setWeekday(cron[4] !== "*" ? cron[4] : 0);
      setDay(cron[2] !== "*" ? cron[2] : 0);
      if (cron[4] !== "*") {
        setFrequency("week");
      } else if (cron[2] !== "*") {
        setFrequency("month");
      } else {
        setFrequency("day");
      }
      setExpiration(expire.format("X"));
    }
  }, [schedule]);

  // create cron format for schedule on input changes
  useEffect(() => {
    const cron = `0 ${time} ${frequency === "month" ? day : "*"} * ${
      frequency === "week" ? weekday : "*"
    }`;
    const newSchedule = frequency === "none" ? "" : cron;
    setSchedule(newSchedule);
  }, [frequency, day, weekday, time]);

  // Convert expiration (epoch) to expire (moment obj)
  useEffect(() => {
    if (expiration) {
      setExpire(moment(expiration, "X"));
    }
  }, [expiration]);

  // Set default dates only once (better perfomance);
  useEffect(() => {
    const min = moment()
      .add(0, "day")
      .set({ hour: 0, minute: 0, second: 0, millisecond: 0 });
    const max = moment()
      .add(10, "years")
      .set({ hour: 0, minute: 0, second: 0, millisecond: 0 });
    const def = moment()
      .add(1, "year")
      .set({ hour: 0, minute: 0, second: 0, millisecond: 0 });
    setMinDate(min);
    setMaxDate(max);
    setDefaultDate(def);
    setExpire(def);
  }, []);

  return (
    <div className="builder__schedule">
      <div className="d-flex">
        <div className="">
          <FormGroup className="m-0">
            <Input
              type="select"
              value={frequency}
              onChange={(e) => {
                if (e.target.value === "none") {
                  setExpiration(null);
                  setExpire(defaultDate);
                  setInvalidScheduleMessage("");
                }
                setFrequency(e.target.value);
              }}
            >
              <option value="none">Not Scheduled</option>
              <option value="day">Every day</option>
              <option value="week">Every week</option>
              <option value="month">Every month</option>
            </Input>
          </FormGroup>
        </div>
        {frequency === "week" && (
          <div className="d-flex align-items-center">
            <span className="mx-2">on</span>
            <FormGroup className="m-0">
              <Input
                type="select"
                value={weekday}
                onChange={(e) => setWeekday(e.target.value)}
              >
                <option value="0">Sunday</option>
                <option value="1">Monday</option>
                <option value="2">Tuesday</option>
                <option value="3">Wednesday</option>
                <option value="4">Thursday</option>
                <option value="5">Friday</option>
                <option value="6">Saturday</option>
              </Input>
            </FormGroup>
          </div>
        )}
        {frequency === "month" && (
          <div className="d-flex align-items-center">
            <span className="mx-2">on the</span>
            <FormGroup className="m-0">
              <Input
                type="select"
                value={day}
                onChange={(e) => setDay(e.target.value)}
              >
                <option value={1}>1st</option>
                <option value={2}>2nd</option>
                <option value={3}>3rd</option>
                <option value={4}>4th</option>
                <option value={5}>5th</option>
                <option value={6}>6th</option>
                <option value={7}>7th</option>
                <option value={8}>8th</option>
                <option value={9}>9th</option>
                <option value={10}>10th</option>
                <option value={11}>11th</option>
                <option value={12}>12th</option>
                <option value={13}>13th</option>
                <option value={14}>14th</option>
                <option value={15}>15th</option>
                <option value={16}>16th</option>
                <option value={17}>17th</option>
                <option value={18}>18th</option>
                <option value={19}>19th</option>
                <option value={20}>20th</option>
                <option value={21}>21st</option>
                <option value={22}>22nd</option>
                <option value={23}>23rd</option>
                <option value={24}>24th</option>
                <option value={25}>25th</option>
                <option value={26}>26th</option>
                <option value={27}>27th</option>
                <option value={28}>28th</option>
              </Input>
            </FormGroup>
          </div>
        )}
        {frequency !== "none" && (
          <div className="d-flex align-items-center">
            <span className="mx-2">at</span>
            <FormGroup className="m-0">
              <Input
                type="select"
                value={time}
                onChange={(e) => setTime(e.target.value)}
              >
                <option value="0">12 AM</option>
                <option value="1">1 AM</option>
                <option value="2">2 AM</option>
                <option value="3">3 AM</option>
                <option value="4">4 AM</option>
                <option value="5">5 AM</option>
                <option value="6">6 AM</option>
                <option value="7">7 AM</option>
                <option value="8">8 AM</option>
                <option value="9">9 AM</option>
                <option value="10">10 AM</option>
                <option value="11">11 AM</option>
                <option value="12">12 PM</option>
                <option value="13">1 PM</option>
                <option value="14">2 PM</option>
                <option value="15">3 PM</option>
                <option value="16">4 PM</option>
                <option value="17">5 PM</option>
                <option value="18">6 PM</option>
                <option value="19">7 PM</option>
                <option value="20">8 PM</option>
                <option value="21">9 PM</option>
                <option value="22">10 PM</option>
                <option value="23">11 PM</option>
              </Input>
            </FormGroup>
            <FormGroup className="m-0 me-1">
              <Input
                type="select"
                value={timezone}
                onChange={(e) => setTimezone(e.target.value)}
              >
                <option value="US/Eastern">US Eastern (New York)</option>
                <option value="US/Central">US Central (Chicago)</option>
                <option value="US/Mountain">US Mountain (Denver)</option>
                <option value="US/Pacific">US Pacific (Los Angeles)</option>
              </Input>
            </FormGroup>
          </div>
        )}
      </div>
      {frequency !== "none" && (
        <div className="mt-3 d-flex">
          <FormGroup>
            <Label className="builder__label me-2">
              Run Until: <sup>*</sup>
            </Label>
            <SingleDatePicker
              id="alt-datepicker"
              date={expire}
              onDateChange={handleExpireDateChange}
              focused={datePickerFocus}
              onFocusChange={() => setDatePickerFocus(!datePickerFocus)}
              required
              numberOfMonths={1}
              openDirection="up"
              isOutsideRange={(date) =>
                date.isBefore(minDate, "day") || date.isAfter(maxDate, "day")
              }
            />
            <small className="form-text text-muted ms-2">
              This extract will run on schedule until the specified date
            </small>
            {invalidScheduleMessage && (
              <small className="form-text text-danger">
                {invalidScheduleMessage}
              </small>
            )}
          </FormGroup>
        </div>
      )}
    </div>
  );
};

Schedule.propTypes = {
  schedule: PropTypes.string,
  setSchedule: PropTypes.func.isRequired,
  timezone: PropTypes.string.isRequired,
  setTimezone: PropTypes.func.isRequired,
  expiration: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  setExpiration: PropTypes.func.isRequired,
};

Schedule.defaultProps = {
  schedule: "",
  expiration: "",
};

export default Schedule;
